import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layouts/DashboardLayout';
import { CodeExecutionEditor } from '../../components/code-execution/CodeExecutionEditor';
import { SkillsLibrary } from '../../components/code-execution/SkillsLibrary';
import { ExecutionHistory } from '../../components/code-execution/ExecutionHistory';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Code2, BookOpen, History, ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';

export function CodeExecutionPage() {
  const { serverId } = useParams<{ serverId: string }>();
  const navigate = useNavigate();
  const [serverName, setServerName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (serverId) {
      loadServer();
    }
  }, [serverId]);

  const loadServer = async () => {
    try {
      const response = await api.get(`/api/servers/${serverId}`);
      setServerName(response.data.name || serverId);
    } catch (error) {
      console.error('Failed to load server:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!serverId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Server ID not provided</p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-6 h-full">
        <div className="mb-6">
          <button
            onClick={() => navigate(`/dashboard/servers/${serverId}`)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Server Details
          </button>
          
          <div>
            <h1 className="text-3xl font-bold">Code Execution</h1>
            <p className="text-gray-600 mt-1">
              Write and execute code to interact with {serverName} using progressive tool loading
            </p>
          </div>
        </div>

        <Tabs defaultValue="editor" className="flex-1">
          <TabsList className="mb-4">
            <TabsTrigger value="editor">
              <Code2 className="h-4 w-4 mr-2" />
              Code Editor
            </TabsTrigger>
            <TabsTrigger value="skills">
              <BookOpen className="h-4 w-4 mr-2" />
              Skills Library
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="h-4 w-4 mr-2" />
              Execution History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="editor">
            <CodeExecutionEditor serverId={serverId} serverName={serverName} />
          </TabsContent>

          <TabsContent value="skills">
            <SkillsLibrary />
          </TabsContent>

          <TabsContent value="history">
            <ExecutionHistory />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

